import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { EvaService } from "src/app/core/services/eva.service";
import { Note, NoteAttachment, NoteContext } from "src/app/domains/note";
import { NoteListParams, NoteService } from "src/app/services/note.service";
import { EVA_OBJECT_TAGS, type TagPreset } from "src/app/shared/constants/tag-presets";
import { onDocumentClickCapture } from "src/app/shared/ds/utils/listen-outside-angular";

/** Chips de filtrage par type d'objet lié */
const NOTE_TYPE_CHIPS = [
  { type: "ALL", label: "Toutes", icon: "heroListBullet" },
  { type: "PROJECT", label: "Chantier", icon: "evaHammer" },
  { type: "INTERVENTION", label: "Intervention", icon: "heroWrench" },
  { type: "OFFER", label: "Vente", icon: "heroDocument" },
  { type: "EXPENSE_REPORT", label: "Dépense", icon: "heroShoppingCart" },
] as const;

/** Mapping chip → object_types backend */
const NOTE_CHIP_GROUPS: Record<string, string[]> = {
  PROJECT: ["PROJECT"],
  INTERVENTION: ["INTERVENTION"],
  OFFER: ["OFFER", "INVOICE", "CUSTOMER", "CATALOG"],
  EXPENSE_REPORT: ["PURCHASE_ORDER", "SUPPLIER_INVOICE", "SUPPLIER", "EMAIL_SCAN"],
};

/** Mapping backend object_type → clé EVA_OBJECT_TAGS */
const OBJECT_TYPE_TO_EVA: Record<string, string> = {
  CUSTOMER: "CLIENT",
  PROJECT: "CHANTIER",
  OFFER: "OFFRE",
  INVOICE: "FACTURE_CLIENT",
  EMPLOYEE: "COLLABORATEUR",
  SUPPLIER: "FOURNISSEUR",
  PURCHASE_ORDER: "BON_COMMANDE",
  INTERVENTION: "INTERVENTION",
  CONTRACT: "CONTRAT",
  CUSTOMER_SITE: "SITE",
  CATALOG: "CATALOGUE",
  ARTICLE: "ARTICLE",
  EXPENSE_REPORT: "NOTE_FRAIS",
  CONTACT: "CONTACT",
};

@Component({
  selector: "app-notes-list",
  templateUrl: "./notes-list.component.html",
  styleUrls: ["./notes-list.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotesListComponent implements OnInit, OnChanges, OnDestroy {
  private destroyRef = inject(DestroyRef);

  // ==========================================================================
  // Inputs / Outputs
  // ==========================================================================

  /** Contexte de rattachement actif (pre-filtre) */
  @Input() context: NoteContext | null = null;

  /** Contexte de la page courante (section automatique "Sur cette page") */
  @Input() set pageContext(value: NoteContext | null) {
    this._pageContext = value;
    this.loadPageContextNotes();
  }
  get pageContext(): NoteContext | null {
    return this._pageContext;
  }
  private _pageContext: NoteContext | null = null;

  /** Emis quand une note est selectionnee */
  @Output() noteSelected = new EventEmitter<string>();

  /** Emis pour creer une nouvelle note */
  @Output() createNote = new EventEmitter<void>();

  /** Émis pour retirer le filtre contextuel */
  @Output() contextFilterRemoved = new EventEmitter<void>();

  /** Demande de remettre le filtre sur l'objet de la page (puce de retour) */
  @Output() contextFilterRestored = new EventEmitter<void>();

  // ==========================================================================
  // State
  // ==========================================================================

  /** Toutes les notes chargées depuis le serveur (sans filtre type) */
  private _allNotes: Note[] = [];

  /** Notes affichées (filtrées client-side par type) */
  notes: Note[] = [];

  pageContextNotes: Note[] = [];
  isLoading: boolean = true;
  totalNotes: number = 0;
  searchTerm: string = "";
  hasMore: boolean = true;
  activeTab: "all" | "pinned" = "all";

  /** Chips de type d'objet lié */
  readonly noteTypeChips = NOTE_TYPE_CHIPS;

  /** Filtre actif par type d'objet (chip) */
  activeTypeFilter: string = "ALL";

  /** Compteurs par chip (calculés depuis _allNotes) */
  noteTypeCounts: Record<string, number> = {};

  /** Puces utiles seulement, même règle que les tâches : « Toutes », celles qui ont des notes et l'active */
  get visibleNoteTypeChips(): (typeof NOTE_TYPE_CHIPS)[number][] {
    return this.noteTypeChips.filter(
      (chip) => chip.type === "ALL" || (this.noteTypeCounts[chip.type] ?? 0) > 0 || chip.type === this.activeTypeFilter,
    );
  }

  /** Tri actif */
  sortBy: "date" | "title" = "date";
  showSortMenu = false;

  private currentSkip: number = 0;
  private readonly PAGE_SIZE = 20;
  private isLoadingMore: boolean = false;

  /** Guard contre les updates post-destroy */
  private isDestroyed = false;

  /** Subject pour la recherche debounce */
  private searchSubject = new Subject<string>();

  @ViewChild("scrollContainer") scrollContainer?: ElementRef<HTMLElement>;

  /** Menu contextuel (clic droit) */
  contextMenu = { visible: false, x: 0, y: 0, note: null as Note | null };

  /** Dialog de confirmation de suppression */
  showDeleteDialog = false;
  deleteConfirmNote: Note | null = null;

  /** Clic à l'extérieur, en capture : le galet arrête la propagation des clics */
  private readonly stopOutsideClick = onDocumentClickCapture(
    () => this.contextMenu.visible || this.showSortMenu,
    (event) => this.onDocumentClick(event),
  );

  constructor(
    private noteService: NoteService,
    private evaService: EvaService,
    private cdr: ChangeDetectorRef,
  ) {}

  // ==========================================================================
  // Lifecycle
  // ==========================================================================

  ngOnInit(): void {
    this.setupSearch();
    this.loadNotes();
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["context"] && !changes["context"].firstChange) {
      this.resetAndReload();
    }
  }

  // ==========================================================================
  // Computed
  // ==========================================================================

  /** IDs des notes déjà affichées dans la section "Sur cette page" */
  private get pageContextNoteIds(): Set<string> {
    return new Set(this.pageContextNotes.map((n) => n.note_id));
  }

  get pinnedNotes(): Note[] {
    const notes = this.notes.filter((n) => n.is_pinned);
    if (this.showPageSection) {
      const ids = this.pageContextNoteIds;
      return notes.filter((n) => !ids.has(n.note_id));
    }
    return notes;
  }

  /**
   * Notes "Autres" : non-épinglées ET non concernées par le contexte de page courant.
   * Règle : une note va dans "Autres" si elle n'est pas épinglée ET pas rattachée
   * à l'objet de la page courante (client, chantier, offre, etc.).
   * L'exclusion des pageContextNotes s'applique uniquement quand la section
   * "Sur cette page" est visible (onglet "Toutes", pas de filtre, pas de recherche).
   */
  get otherNotes(): Note[] {
    const notes = this.notes.filter((n) => !n.is_pinned);
    if (this.showPageSection) {
      const ids = this.pageContextNoteIds;
      return notes.filter((n) => !ids.has(n.note_id));
    }
    return notes;
  }

  get showEmptyState(): boolean {
    return !this.isLoading && this.notes.length === 0 && !this.searchTerm;
  }

  /** Vrai si la section page doit être affichée (notes trouvées, pas de filtre actif, pas de recherche, onglet "all") */
  get showPageSection(): boolean {
    return (
      this.pageContextNotes.length > 0 &&
      !this.context &&
      !this.searchTerm &&
      this.activeTab === "all" &&
      this.activeTypeFilter === "ALL"
    );
  }

  /** Preset eva-tag pour le titre de la section "Épinglées" */
  readonly pinnedSectionPreset: TagPreset = { label: "Épinglé", severity: "warning", icon: "evaPin" };

  /** Preset eva-tag pour le titre de la section "Autres" (notes non épinglées) */
  readonly recentSectionPreset: TagPreset = { label: "Autres", severity: "secondary", icon: "heroListBullet" };

  /** Preset eva-tag pour la section "Sur cette page" */
  get pageContextPreset(): TagPreset {
    const evaKey = OBJECT_TYPE_TO_EVA[this._pageContext?.objectType ?? ""] || this._pageContext?.objectType || "";
    const base = EVA_OBJECT_TAGS[evaKey] ?? { label: "Page", severity: "secondary" as const };
    // Toujours afficher uniquement le nom de l'objet, sans préfixe de type
    const label = this._pageContext?.objectLabel ?? base.label;
    return { ...base, label };
  }

  get showNoResults(): boolean {
    return !this.isLoading && this.notes.length === 0 && !!this.searchTerm;
  }

  // ==========================================================================
  // Search
  // ==========================================================================

  private setupSearch(): void {
    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((term) => {
        this.searchTerm = term;
        this.resetAndReload();
      });
  }

  onSearchInput(value: string): void {
    this.searchSubject.next(value);
  }

  // ==========================================================================
  // Data loading
  // ==========================================================================

  private buildParams(): NoteListParams {
    const params: NoteListParams = {
      limit: this.PAGE_SIZE,
      skip: this.currentSkip,
    };

    if (this.searchTerm) {
      params.q = this.searchTerm;
    }

    if (this.context) {
      params.object_type = this.context.objectType;
      params.object_id = this.context.objectId;
    }

    if (this.activeTab === "pinned") {
      params.is_pinned = true;
    }

    return params;
  }

  loadPageContextNotes(): void {
    if (!this._pageContext) {
      this.pageContextNotes = [];
      this.cdr.markForCheck();
      return;
    }
    const params: Parameters<NoteService["getAll"]>[0] = {
      limit: 10,
      skip: 0,
      object_type: this._pageContext.objectType,
    };
    // Sur une page de liste (pas d'objectId), on agrège toutes les notes du type
    if (this._pageContext.objectId) {
      params.object_id = this._pageContext.objectId;
    }
    this.noteService
      .getAll(params)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          if (this.isDestroyed) return;
          this.pageContextNotes = response.data;
          this.cdr.markForCheck();
        },
        error: () => {
          this.pageContextNotes = [];
          this.cdr.markForCheck();
        },
      });
  }

  loadNotes(): void {
    this.isLoading = true;
    this.cdr.markForCheck();

    this.noteService
      .getAll(this.buildParams())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          if (this.isDestroyed) return;
          if (this.currentSkip === 0) {
            this._allNotes = this.applySortBy(response.data);
          } else {
            this._allNotes = this.applySortBy([...this._allNotes, ...response.data]);
          }
          this.computeTypeCounts(this._allNotes);
          this.notes = this.applyTypeFilter(this._allNotes);
          this.totalNotes = response.total;
          this.hasMore = response.data.length >= this.PAGE_SIZE;
          this.isLoading = false;
          this.isLoadingMore = false;
          this.cdr.markForCheck();
        },
        error: () => {
          if (this.isDestroyed) return;
          this.isLoading = false;
          this.isLoadingMore = false;
          this.evaService.showError("Impossible de charger les notes", "loadNotes", "");
          this.cdr.markForCheck();
        },
      });
  }

  /** Recharge la liste depuis le debut */
  reload(): void {
    this.currentSkip = 0;
    this.loadNotes();
  }

  private resetAndReload(): void {
    this.currentSkip = 0;
    this._allNotes = [];
    this.notes = [];
    this.hasMore = true;
    this.loadNotes();
  }

  // ==========================================================================
  // Infinite scroll
  // ==========================================================================

  onScroll(event: Event): void {
    if (!this.hasMore || this.isLoadingMore || this.isLoading) return;

    const element = event.target as HTMLElement;
    const threshold = 100;
    const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;

    if (distanceFromBottom < threshold) {
      this.loadMore();
    }
  }

  private loadMore(): void {
    this.isLoadingMore = true;
    this.currentSkip += this.PAGE_SIZE;
    this.loadNotes();
  }

  // ==========================================================================
  // Context filter
  // ==========================================================================

  removeContextFilter(): void {
    this.contextFilterRemoved.emit();
  }

  /** Puce de retour : remet le filtre sur l'objet de la page */
  restoreContextFilter(): void {
    this.contextFilterRestored.emit();
  }

  // ==========================================================================
  // Actions
  // ==========================================================================

  openNote(noteId: string): void {
    this.noteSelected.emit(noteId);
  }

  onCreateNote(): void {
    this.createNote.emit();
  }

  onTabChange(tab: string): void {
    this.activeTab = tab as "all" | "pinned";
    this.resetAndReload();
  }

  setTypeFilter(type: string): void {
    this.activeTypeFilter = type;
    this.notes = this.applyTypeFilter(this._allNotes);
    this.cdr.markForCheck();
  }

  // ==========================================================================
  // Tri
  // ==========================================================================

  setSortBy(sort: "date" | "title"): void {
    this.sortBy = sort;
    this.showSortMenu = false;
    this._allNotes = this.applySortBy([...this._allNotes]);
    this.notes = this.applyTypeFilter(this._allNotes);
    this.cdr.markForCheck();
  }

  toggleSortMenu(): void {
    this.showSortMenu = !this.showSortMenu;
    this.cdr.markForCheck();
  }

  get sortLabel(): string {
    return this.sortBy === "title" ? "Titre" : "Date";
  }

  /** Filtre les notes par chip type (client-side) */
  private applyTypeFilter(notes: Note[]): Note[] {
    if (this.activeTypeFilter === "ALL") return notes;
    const types = NOTE_CHIP_GROUPS[this.activeTypeFilter] ?? [];
    return notes.filter((n) => n.attachments?.some((a) => types.includes(a.object_type)));
  }

  /** Calcule les compteurs par chip depuis la liste complète */
  private computeTypeCounts(notes: Note[]): void {
    const counts: Record<string, number> = {};
    for (const note of notes) {
      const matchedChips = new Set<string>();
      for (const att of note.attachments ?? []) {
        for (const [chip, group] of Object.entries(NOTE_CHIP_GROUPS)) {
          if (group.includes(att.object_type)) {
            matchedChips.add(chip);
          }
        }
      }
      for (const chip of matchedChips) {
        counts[chip] = (counts[chip] || 0) + 1;
      }
    }
    this.noteTypeCounts = counts;
  }

  private applySortBy(notes: Note[]): Note[] {
    return notes.sort((a, b) => {
      if (this.sortBy === "title") {
        return (a.title || "").localeCompare(b.title || "", "fr");
      }
      // Par défaut : date décroissante (plus récent en premier)
      const dateA = new Date(a.updated_on).getTime();
      const dateB = new Date(b.updated_on).getTime();
      return dateB - dateA;
    });
  }

  // ==========================================================================
  // Context menu (clic droit)
  // ==========================================================================

  onDocumentClick(event: MouseEvent): void {
    // Le menu et le bouton de tri gèrent eux-mêmes leurs clics
    if ((event.target as HTMLElement | null)?.closest(".notes-list__context-menu, .notes-list__sort")) return;
    this.closeContextMenu();
    if (this.showSortMenu) {
      this.showSortMenu = false;
      this.cdr.markForCheck();
    }
  }

  @HostListener("document:keydown.escape", ["$event"])
  onEscapeContextMenu(event: KeyboardEvent): void {
    if (this.contextMenu.visible) {
      event.preventDefault();
      this.closeContextMenu();
    }
  }

  onNoteRightClick(event: MouseEvent, note: Note): void {
    event.preventDefault();
    event.stopPropagation();
    this.contextMenu = { visible: true, x: event.clientX, y: event.clientY, note };
    this.cdr.markForCheck();
  }

  closeContextMenu(): void {
    if (this.contextMenu.visible) {
      this.contextMenu = { ...this.contextMenu, visible: false, note: null };
      this.cdr.markForCheck();
    }
  }

  contextMenuOpen(note: Note): void {
    this.closeContextMenu();
    this.openNote(note.note_id);
  }

  contextMenuTogglePin(note: Note): void {
    this.closeContextMenu();
    this.noteService
      .update(note.note_id, { is_pinned: !note.is_pinned })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          const allIdx = this._allNotes.findIndex((n) => n.note_id === updated.note_id);
          if (allIdx >= 0) {
            this._allNotes[allIdx] = updated;
            this._allNotes = [...this._allNotes];
          }
          this.notes = this.applyTypeFilter(this._allNotes);
          this.cdr.markForCheck();
        },
        error: () => {
          this.evaService.showError("Impossible de modifier l'épinglage", "togglePin", "");
        },
      });
  }

  contextMenuDelete(note: Note): void {
    this.closeContextMenu();
    this.deleteConfirmNote = note;
    this.showDeleteDialog = true;
    this.cdr.markForCheck();
  }

  confirmDelete(): void {
    if (!this.deleteConfirmNote) return;
    this.showDeleteDialog = false;
    this.noteService
      .delete(this.deleteConfirmNote.note_id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.evaService.showSuccess("Note archivée");
          this.deleteConfirmNote = null;
          this.reload();
        },
        error: () => {
          this.evaService.showError("Erreur lors de la suppression", "deleteNote", "");
          this.deleteConfirmNote = null;
        },
      });
  }

  // ==========================================================================
  // Helpers
  // ==========================================================================

  /** Retire le markdown basique pour l'aperçu en préservant la structure */
  stripMarkdown(text: string): string {
    if (!text) return "";
    return text
      .replace(/```[\s\S]*?```/g, "") // code blocks
      .replace(/^---+$/gm, "") // horizontal rules
      .replace(/^#{1,6}\s+/gm, "") // headers
      .replace(/^>\s+/gm, "") // blockquotes
      .replace(/^- \[[ xX]\]\s*/gm, "• ") // checkboxes → bullet
      .replace(/^\d+\.\s+/gm, "• ") // numbered lists → bullet
      .replace(/\*\*(.+?)\*\*/g, "$1") // bold
      .replace(/\*(.+?)\*/g, "$1") // italic
      .replace(/`(.+?)`/g, "$1") // inline code
      .replace(/\[(.+?)\]\(.+?\)/g, "$1") // links
      .replace(/^[-*+]\s+/gm, "• ") // list items → bullet (conserve le saut de ligne)
      .replace(/\n{2,}/g, "\n") // lignes vides → saut simple (évite de gaspiller le clamp à 2 lignes)
      .trim();
  }

  /** Formate une date au format suisse */
  formatDate(date: Date | string): string {
    if (!date) return "";
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, "0");
    const month = (d.getMonth() + 1).toString().padStart(2, "0");
    const year = d.getFullYear();
    const hours = d.getHours().toString().padStart(2, "0");
    const minutes = d.getMinutes().toString().padStart(2, "0");
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  }

  /** Formate une date en relatif (Aujourd'hui, Hier, date courte) */
  formatRelativeDate(date: Date | string): string {
    if (!date) return "";
    const d = new Date(date);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

    if (diffHours < 1) {
      return "À l'instant";
    }
    if (diffHours < 2) {
      return "Il y a 1h";
    }
    if (diffHours < 24) {
      return `Il y a ${diffHours}h`;
    }
    if (diffHours < 48) {
      return "Hier";
    }
    if (diffHours < 168) {
      const days = Math.floor(diffHours / 24);
      return `Il y a ${days}j`;
    }

    // Format date courte
    const day = d.getDate().toString().padStart(2, "0");
    const month = (d.getMonth() + 1).toString().padStart(2, "0");
    return `${day}.${month}`;
  }

  /** Labels des rattachements tronques */
  getAttachmentLabels(attachments: Note["attachments"]): { display: string[]; overflow: number } {
    if (!attachments || attachments.length === 0) {
      return { display: [], overflow: 0 };
    }
    const maxDisplay = 2;
    const display = attachments
      .slice(0, maxDisplay)
      .map((a) => a.label || a.object_type)
      .filter(Boolean);
    const overflow = Math.max(0, attachments.length - maxDisplay);
    return { display, overflow };
  }

  trackByNoteId(index: number, note: Note): string {
    return note.note_id;
  }

  /** Retourne le preset eva-tag pour un attachment */
  getAttachmentPreset(att: NoteAttachment): TagPreset {
    const evaKey = OBJECT_TYPE_TO_EVA[att.object_type] || att.object_type;
    const base = EVA_OBJECT_TAGS[evaKey] ?? { label: att.object_type, severity: "secondary" as const };
    return { ...base, label: att.label || att.object_id };
  }

  private visibleAttachmentsCache = new WeakMap<
    Note,
    { objectId: string | null | undefined; list: NoteAttachment[] }
  >();

  /** Objets liés à afficher sur la carte, sans celui que le filtre affiche déjà (comme les tâches) */
  visibleAttachments(note: Note, filteredObjectId: string | null | undefined): NoteAttachment[] {
    const cached = this.visibleAttachmentsCache.get(note);
    if (cached && cached.objectId === filteredObjectId) return cached.list;
    const list = (note.attachments ?? []).filter((att) => !filteredObjectId || att.object_id !== filteredObjectId);
    this.visibleAttachmentsCache.set(note, { objectId: filteredObjectId, list });
    return list;
  }

  /** Tooltip listant les objets liés au-delà du premier */
  getOverflowTooltip(attachments: NoteAttachment[]): string {
    return attachments
      .slice(1)
      .map((att) => att.label || att.object_id)
      .join("\n");
  }
}
